import React, { useEffect, useState, useMemo } from 'react';
import api, { trackFunnel } from '../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Loader2, Clock, Users, FlaskConical, ListChecks, Download, RefreshCw,
  Plus, Trash2, Sparkles, Flame, ShieldCheck,
} from 'lucide-react';

const formatHours = (h) => {
  if (!h) return '0h';
  if (h < 1) return `${Math.round(h * 60)}min`;
  return `${h}h`;
};

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <Card className="p-4" data-testid={`releasepack-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start gap-3">
      <div className={`p-2 rounded-lg ${accent || 'bg-slate-100 text-slate-700'}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
        <p className="text-xs text-slate-500 mt-0.5">{label}</p>
        {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
      </div>
    </div>
  </Card>
);

export const ReleasePackTab = ({ projectId }) => {
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [newCategory, setNewCategory] = useState('Custom');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}/release-pack`);
      setPack(res.data);
    } catch (e) {
      toast.error('Failed to load release pack');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); trackFunnel('release_pack_opened', { project_id: projectId }); }, [projectId]);

  const handleRegenerate = async () => {
    if (!window.confirm('Regenerate will reset smoke selection and checklist to defaults. Continue?')) return;
    setRegenerating(true);
    try {
      await api.post(`/projects/${projectId}/release-pack/regenerate`);
      toast.success('Release pack regenerated');
      await load();
    } catch {
      toast.error('Failed to regenerate');
    } finally {
      setRegenerating(false);
    }
  };

  const toggleSmoke = async (tc_id, currentlySmoke) => {
    // optimistic update
    setPack(prev => ({
      ...prev,
      smoke_tc_ids: currentlySmoke ? prev.smoke_tc_ids.filter(t => t !== tc_id) : [...prev.smoke_tc_ids, tc_id],
    }));
    try {
      await api.patch(`/projects/${projectId}/release-pack/smoke`, { tc_id, smoke: !currentlySmoke });
    } catch {
      toast.error('Failed to update smoke flag');
      load();
    }
  };

  const toggleChecklist = async (item_id, checked) => {
    setPack(prev => ({
      ...prev,
      checklist: prev.checklist.map(it => it.id === item_id ? { ...it, checked } : it),
    }));
    try {
      await api.patch(`/projects/${projectId}/release-pack/checklist`, { action: 'toggle', item_id, checked });
    } catch {
      toast.error('Failed to update checklist');
      load();
    }
  };

  const addChecklistItem = async () => {
    const txt = newItem.trim();
    if (!txt) return;
    try {
      const res = await api.patch(`/projects/${projectId}/release-pack/checklist`, {
        action: 'add', item: txt, category: newCategory || 'Custom',
      });
      setPack(prev => ({ ...prev, checklist: res.data.checklist }));
      setNewItem('');
      toast.success('Item added');
    } catch {
      toast.error('Failed to add item');
    }
  };

  const deleteChecklistItem = async (item_id) => {
    const prevChecklist = pack.checklist;
    setPack(prev => ({ ...prev, checklist: prev.checklist.filter(it => it.id !== item_id) }));
    try {
      await api.patch(`/projects/${projectId}/release-pack/checklist`, { action: 'delete', item_id });
    } catch {
      toast.error('Failed to delete');
      setPack(prev => ({ ...prev, checklist: prevChecklist }));
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/release-pack/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `release_pack_${projectId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const groupedChecklist = useMemo(() => {
    if (!pack?.checklist) return {};
    const groups = {};
    pack.checklist.forEach(it => {
      groups[it.category] = groups[it.category] || [];
      groups[it.category].push(it);
    });
    return groups;
  }, [pack]);

  const checklistProgress = useMemo(() => {
    if (!pack?.checklist?.length) return { done: 0, total: 0, pct: 0 };
    const done = pack.checklist.filter(it => it.checked).length;
    return { done, total: pack.checklist.length, pct: Math.round((done / pack.checklist.length) * 100) };
  }, [pack]);

  if (loading) {
    return <div className="flex items-center justify-center py-20" data-testid="releasepack-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  if (!pack) return null;

  const eff = pack.effort || {};
  const smokeSet = new Set(pack.smoke_tc_ids || []);
  const allTcs = pack.smoke_testcases || []; // we have at least the smoke tcs; we'll need all for the toggle view

  // We need full TC list for the regression toggle. Load via a side fetch when needed.
  return (
    <div className="space-y-6" data-testid="release-pack-tab">
      {/* Header bar */}
      <Card className="p-4 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg"><Sparkles className="h-5 w-5 text-amber-300" /></div>
            <div>
              <h2 className="font-heading text-lg font-bold">Release Pack</h2>
              <p className="text-xs text-slate-300">Regression suite, release checklist & effort estimation — ready for sign-off</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={handleRegenerate} disabled={regenerating} data-testid="releasepack-regenerate-btn">
              {regenerating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              Regenerate
            </Button>
            <Button size="sm" onClick={handleExport} data-testid="releasepack-export-btn" className="bg-blue-600 hover:bg-blue-700">
              <Download className="h-4 w-4 mr-1.5" />Export Excel
            </Button>
          </div>
        </div>
      </Card>

      {/* Effort Estimation */}
      <section>
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Clock className="h-3.5 w-3.5" />Test Effort Estimation
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={FlaskConical} label="Total Test Cases" value={eff.total_testcases || 0}
            sub={`${eff.breakdown?.Complex || 0} Complex · ${eff.breakdown?.Medium || 0} Medium · ${eff.breakdown?.Simple || 0} Simple`}
            accent="bg-blue-100 text-blue-700" />
          <StatCard icon={Clock} label="Total Effort" value={formatHours(eff.total_hours)}
            sub={`Rates: S ${eff.rates_minutes?.Simple}m · M ${eff.rates_minutes?.Medium}m · C ${eff.rates_minutes?.Complex}m`}
            accent="bg-amber-100 text-amber-700" />
          <StatCard icon={Users} label="Person-Days" value={eff.person_days || 0}
            sub="1 QA · 6h productive/day" accent="bg-emerald-100 text-emerald-700" />
          <StatCard icon={Users} label="Calendar Days" value={eff.team_2qa_days || 0}
            sub="With 2 QA engineers" accent="bg-violet-100 text-violet-700" />
        </div>
      </section>

      {/* Regression Suite (Smoke) */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
            <Flame className="h-3.5 w-3.5" />Smoke Suite ({smokeSet.size} of {pack.total_testcases})
          </h3>
          <p className="text-xs text-slate-400">Tap the flame to add / remove a test case</p>
        </div>
        <Card className="overflow-hidden">
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <FullTCTable projectId={projectId} smokeSet={smokeSet} onToggle={toggleSmoke} />
          </div>
        </Card>
      </section>

      {/* Release Checklist */}
      <section>
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
            <ListChecks className="h-3.5 w-3.5" />Release Checklist
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">{checklistProgress.done} / {checklistProgress.total} ({checklistProgress.pct}%)</span>
            <div className="w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${checklistProgress.pct}%` }} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(groupedChecklist).map(([cat, items]) => (
            <Card key={cat} className="p-4" data-testid={`checklist-category-${cat.toLowerCase().replace(/\s/g, '-')}`}>
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-blue-600" />{cat}
              </p>
              <ul className="space-y-2">
                {items.map(it => (
                  <li key={it.id} className="flex items-start gap-2 group" data-testid={`checklist-item-${it.id}`}>
                    <input
                      type="checkbox"
                      checked={!!it.checked}
                      onChange={(e) => toggleChecklist(it.id, e.target.checked)}
                      className="mt-1 rounded border-slate-300 cursor-pointer"
                      data-testid={`checklist-checkbox-${it.id}`}
                    />
                    <span className={`text-sm flex-1 ${it.checked ? 'line-through text-slate-400' : 'text-slate-700'}`}>{it.item}</span>
                    {it.source === 'user' && (
                      <button onClick={() => deleteChecklistItem(it.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500" data-testid={`checklist-delete-${it.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>

        {/* Add custom item */}
        <Card className="p-4 mt-3" data-testid="checklist-add-card">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Add a custom item</p>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              value={newCategory}
              onChange={e => setNewCategory(e.target.value)}
              placeholder="Category"
              className="w-40 h-9 text-sm"
              data-testid="checklist-new-category"
            />
            <Input
              value={newItem}
              onChange={e => setNewItem(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }}
              placeholder="e.g. Marketing team notified for release"
              className="flex-1 min-w-[200px] h-9 text-sm"
              data-testid="checklist-new-item-input"
            />
            <Button size="sm" onClick={addChecklistItem} disabled={!newItem.trim()} data-testid="checklist-add-btn">
              <Plus className="h-4 w-4 mr-1" />Add
            </Button>
          </div>
        </Card>
      </section>
    </div>
  );
};

// Sub-component: full TC table that fetches all testcases for the project
const FullTCTable = ({ projectId, smokeSet, onToggle }) => {
  const [tcs, setTcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | smoke

  useEffect(() => {
    let mounted = true;
    api.get(`/projects/${projectId}/testcases`).then(res => {
      if (mounted) { setTcs(res.data || []); setLoading(false); }
    }).catch(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [projectId]);

  const visible = filter === 'smoke' ? tcs.filter(tc => smokeSet.has(tc.tc_id)) : tcs;

  if (loading) return <div className="p-8 text-center text-slate-400 text-sm">Loading test cases…</div>;
  if (!tcs.length) return <div className="p-8 text-center text-slate-400 text-sm">No test cases yet — generate them first from the Test Cases tab.</div>;

  return (
    <div>
      <div className="flex items-center gap-2 p-3 border-b border-slate-100 bg-slate-50/50 sticky top-0 z-10">
        <button onClick={() => setFilter('all')} className={`px-2.5 py-1 text-xs rounded-md ${filter === 'all' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-200'}`} data-testid="tc-filter-all">All ({tcs.length})</button>
        <button onClick={() => setFilter('smoke')} className={`px-2.5 py-1 text-xs rounded-md ${filter === 'smoke' ? 'bg-orange-600 text-white' : 'text-slate-600 hover:bg-slate-200'}`} data-testid="tc-filter-smoke">Smoke only ({smokeSet.size})</button>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-slate-50 border-b border-slate-100 sticky top-[41px]">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-slate-600 w-16">Smoke</th>
            <th className="text-left px-3 py-2 font-medium text-slate-600">TC ID</th>
            <th className="text-left px-3 py-2 font-medium text-slate-600">Module</th>
            <th className="text-left px-3 py-2 font-medium text-slate-600">Title</th>
            <th className="text-left px-3 py-2 font-medium text-slate-600">Priority</th>
            <th className="text-left px-3 py-2 font-medium text-slate-600">Type</th>
          </tr>
        </thead>
        <tbody>
          {visible.map(tc => {
            const isSmoke = smokeSet.has(tc.tc_id);
            return (
              <tr key={tc.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                <td className="px-3 py-2">
                  <button
                    onClick={() => onToggle(tc.tc_id, isSmoke)}
                    className={`p-1.5 rounded-md transition-all ${isSmoke ? 'bg-orange-500 text-white shadow-sm' : 'bg-slate-100 text-slate-300 hover:bg-slate-200'}`}
                    data-testid={`smoke-toggle-${tc.tc_id}`}
                    title={isSmoke ? 'Remove from Smoke' : 'Add to Smoke'}
                  >
                    <Flame className="h-3.5 w-3.5" />
                  </button>
                </td>
                <td className="px-3 py-2 font-mono text-slate-700">{tc.tc_id}</td>
                <td className="px-3 py-2 text-slate-600 max-w-[140px] truncate">{tc.module}</td>
                <td className="px-3 py-2 text-slate-700 max-w-[400px] truncate">{tc.scenario_title}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                    tc.priority === 'High' ? 'bg-red-50 text-red-700' :
                    tc.priority === 'Medium' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'
                  }`}>{tc.priority}</span>
                </td>
                <td className="px-3 py-2 text-slate-500">{tc.type}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
